import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import {
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Radio,
  RadioGroup,
} from '@mui/material';
import React, { useState, useEffect } from 'react';
import { Controller, UseFormWatch } from 'react-hook-form';
import { motion } from 'framer-motion';

import { DeliveryMethod, OrderCreationAttributes } from '../../models/Order';
import InPostGeoWidget from '../../components/inpost/InPostGeoWidget';
import InPostPointDetails from '../../components/inpost/InPostPointDetails';
import { InPostPoint } from '../../models/InPostPoint';
import translateDeliveryMethod from '../../helperFunctions/translateDeliveryMethod';
import getIconForDeliveryMethod from '../../helperFunctions/getIconForDeliveryMethod';
import { useConfig } from '../../utils/useConfig';

// Dodaj setValue do props komponentu
type Props = {
  control: any;
  errors: any;
  watch: UseFormWatch<
    OrderCreationAttributes & {
      acceptTerms: boolean;
    }
  >;
  setValue: any; // Dodaj tę linię
  deliveryCosts: { deliveryCost: number; deliveryMethod: DeliveryMethod }[];
  setDeliveryPrice: (price: number) => void;
};

const DeliveryMethodForm = ({
  control,
  errors,
  watch,
  setValue, // Dodaj tę linię
  deliveryCosts,
  setDeliveryPrice
}: Props) => {
  const config = useConfig();

  const selectedDeliveryMethod = watch('deliveryMethod');
  const paczkomatValue = watch('paczkomat'); // Dodajemy obserwowanie wartości paczkomatu
  const [selectedPoint, setSelectedPoint] = useState<InPostPoint | null>(null);

  // Dodajmy efekt do debugowania
  useEffect(() => {}, [
    selectedDeliveryMethod,
    paczkomatValue,
    selectedPoint,
    errors,
  ]);

  React.useEffect(() => {
    if (deliveryCosts.length === 0 || deliveryCosts === null) {
      return;
    }
    const selectedMethod = deliveryCosts.find(
      ({ deliveryMethod }) => deliveryMethod === selectedDeliveryMethod,
    );
    if (selectedMethod) {
      setDeliveryPrice(selectedMethod.deliveryCost);
    }
  }, [selectedDeliveryMethod, deliveryCosts, setDeliveryPrice]);

  // Resetujemy wybrany punkt, gdy zmieniamy metodę dostawy na inną niż Paczkomat
  React.useEffect(() => {
    if (selectedDeliveryMethod !== DeliveryMethod.PACZKOMAT) {
      setSelectedPoint(null);
    }
  }, [selectedDeliveryMethod]);

  // Zmodyfikuj handlePointSelect aby używać setValue bezpośrednio
  const handlePointSelect = (point: InPostPoint | null) => {
    setSelectedPoint(point);

    // Zabezpieczenie przed przypadkowym wyczyszczeniem wartości
    if (selectedDeliveryMethod === DeliveryMethod.PACZKOMAT) {
      if (point && point.name) {
        // Używamy bezpośrednio setValue przekazanego z useForm
        setValue('inpostName', point.name, {
          shouldValidate: true,
          shouldDirty: true,
          shouldTouch: true,
        });

        // Również zaktualizuj paczkomat dla zachowania kompatybilności
        setValue('paczkomat', point.name, {
          shouldValidate: true,
          shouldDirty: true,
          shouldTouch: true,
        });

        // Zapisz również w sessionStorage jako zabezpieczenie
        try {
          sessionStorage.setItem('selectedPaczkomat', point.name);
          sessionStorage.setItem(
            'paczkomatTimestamp',
            new Date().toISOString(),
          );
        } catch (e) {
          console.warn('Nie można zapisać paczkomatu w sessionStorage:', e);
        }
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4 },
    },
  };

  // Ten efekt będzie synchronizować wartość paczkomatu z komponentem nadrzędnym
  // i zapewni, że wartość nie zostanie utracona
  useEffect(() => {
    // Upewnij się, że wartość jest ustawiona gdy komponent się renderuje
    const currentPackomatValue = watch('paczkomat');

    // Jeśli mamy już wybrany paczkomat w formularzu, ale nie mamy go w stanie lokalnym
    if (currentPackomatValue && !selectedPoint) {
      // Tworzy podstawowy obiekt punktu na podstawie nazwy
      // To zapewni, że formularz będzie miał minimum potrzebnych informacji
      setSelectedPoint({
        name: currentPackomatValue,
      } as InPostPoint);
    }
  }, [watch, selectedPoint]);

  // Ten efekt zapewni, że reguły walidacji dla paczkomatu są zawsze aktualne
  useEffect(() => {
    // Rejestrujemy pole z odpowiednimi regułami walidacji
    const updateValidation = () => {
      // Re-rejestrujemy pole z aktualnymi regułami walidacji
      control.register('paczkomat', {
        required:
          selectedDeliveryMethod === DeliveryMethod.PACZKOMAT
            ? 'Wybór paczkomatu jest wymagany'
            : false,
      });

      // Wymuszamy weryfikację pola, jeśli wybrano Paczkomat
      if (selectedDeliveryMethod === DeliveryMethod.PACZKOMAT) {
        // Logowanie dla debugowania
      }
    };

    updateValidation();
  }, [selectedDeliveryMethod, control, watch]);

  return (
    <div className="p-5">
      {deliveryCosts.length === 0 ? (
        <motion.div
          className="p-6 flex flex-col items-center justify-center text-center"
          variants={itemVariants}
        >
          <div className="rounded-full bg-amber-100 p-3 mb-4">
            <LocalShippingIcon className="text-amber-500" />
          </div>
          <h3 className="text-gray-800 font-medium mb-2">
            Brak dostępnych metod dostawy
          </h3>
          <p className="text-gray-500 text-sm">
            Produkty w Twoim koszyku nie mogą zostać dostarczone standardową
            metodą. Skontaktuj się z nami, aby ustalić szczegóły dostawy.
          </p>
        </motion.div>
      ) : (
        <FormControl
          component="fieldset"
          className="w-full"
          error={!!errors.deliveryMethod}
        >
          <motion.div variants={itemVariants}>
            <FormLabel
              component="legend"
              className="text-gray-700 font-medium mb-4"
            >
              Wybierz sposób dostawy
            </FormLabel>
          </motion.div>

          <Controller
            name="deliveryMethod"
            control={control}
            defaultValue={deliveryCosts[0]?.deliveryMethod || ''}
            rules={{
              required: 'Wybór sposobu dostawy jest wymagany',
            }}
            render={({ field, fieldState }) => (
              <>
                <RadioGroup {...field} className="space-y-3">
                  {deliveryCosts.map(
                    ({ deliveryMethod, deliveryCost }, index) =>
                      deliveryCost !== -1 && (
                        <motion.div
                          key={deliveryMethod}
                          variants={itemVariants}
                          custom={index}
                          whileHover={{ y: -2 }}
                          transition={{ duration: 0.2 }}
                          className="w-full"
                        >
                          <div
                            className={`
                              p-3 border rounded-lg transition-all w-full
                              ${
                                selectedDeliveryMethod === deliveryMethod
                                  ? `border-primary bg-${config.theme.mainColor}-50 shadow-sm`
                                  : 'border-gray-200 hover:border-gray-300'
                              }
                            `}
                          >
                            <FormControlLabel
                              value={deliveryMethod}
                              control={
                                <Radio
                                  color="primary"
                                  className="text-primary"
                                />
                              }
                              label={
                                <div className="flex flex-col sm:flex-row sm:items-center w-full justify-between">
                                  <div className="flex items-center flex-1">
                                    <img
                                      src={getIconForDeliveryMethod(
                                        deliveryMethod,
                                      )}
                                      alt={translateDeliveryMethod(
                                        deliveryMethod,
                                      )}
                                      className="h-8 mr-3 object-contain"
                                    />
                                    <span className="text-gray-800 font-medium">
                                      {translateDeliveryMethod(deliveryMethod)}
                                    </span>
                                  </div>
                                  <div className="text-primary font-bold mt-2 sm:mt-0 ml-2">
                                    {deliveryCost.toFixed(2)} zł
                                  </div>
                                </div>
                              }
                              className="w-full m-0"
                            />
                          </div>
                        </motion.div>
                      ),
                  )}
                </RadioGroup>
                {fieldState.error && (
                  <FormHelperText className="text-red-500 mt-2">
                    {fieldState.error.message}
                  </FormHelperText>
                )}
              </>
            )}
          />

          {selectedDeliveryMethod === DeliveryMethod.PACZKOMAT && (
            <motion.div
              className="mt-4"
              variants={itemVariants}
              initial="hidden"
              animate="visible"
              exit="hidden"
            >
              <div
                className={`bg-${config.theme.mainColor}-50 p-4 rounded-lg border border-${config.theme.mainColor}-100 mb-4`}
              >
                <h4
                  className={`text-sm font-medium text-${config.theme.mainColor}-800 mb-1`}
                >
                  Wybierz paczkomat
                </h4>
                <p className={`text-xs text-${config.theme.mainColor}-600`}>
                  Kliknij poniżej aby wybrać paczkomat, do którego dostarczymy
                  Twoje zamówienie.
                </p>
              </div>

              <div className="border border-gray-200 rounded-lg overflow-hidden shadow-sm">
                <InPostGeoWidget onPointSelect={handlePointSelect} />
              </div>

              {/* Dodajemy informację o stanie wyboru paczkomatu */}
              {selectedDeliveryMethod === DeliveryMethod.PACZKOMAT &&
                !selectedPoint && (
                  <div className="mt-2 bg-yellow-50 border border-yellow-100 rounded p-2 text-sm text-yellow-700">
                    Wybierz paczkomat na mapie, aby kontynuować zamówienie
                  </div>
                )}

              {selectedDeliveryMethod === DeliveryMethod.PACZKOMAT &&
                selectedPoint && (
                  <div className="mt-2 bg-green-50 border border-green-100 rounded p-2 text-sm text-green-700">
                    Wybrany paczkomat: {selectedPoint.name}
                  </div>
                )}

              <Controller
                name="paczkomat"
                control={control}
                defaultValue=""
                rules={{
                  required:
                    selectedDeliveryMethod === DeliveryMethod.PACZKOMAT
                      ? 'Wybór paczkomatu jest wymagany'
                      : false,
                  validate: (value) => {
                    if (
                      selectedDeliveryMethod === DeliveryMethod.PACZKOMAT &&
                      (!value || value.trim() === '')
                    ) {
                      return 'Wybór paczkomatu jest wymagany';
                    }
                    return true;
                  },
                }}
                render={({ field }) => (
                  <input
                    type="hidden"
                    id="paczkomat"
                    name="paczkomat"
                    value={field.value || ''}
                    onChange={field.onChange}
                    ref={field.ref}
                  />
                )}
              />

              {/* Wyświetlenie informacji o wybranym paczkomacie */}
              {selectedPoint && <InPostPointDetails point={selectedPoint} />}
            </motion.div>
          )}

          {selectedDeliveryMethod === DeliveryMethod.PACZKOMAT && (
            <div className="mt-3 border-t border-gray-200 pt-3">
              <label
                htmlFor="paczkomat-field"
                className="block text-sm font-medium text-gray-700"
              >
                Wybrany paczkomat
              </label>

              <input
                id="paczkomat-field"
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
                value={selectedPoint?.name || ''}
                onChange={(e) => {
                  // Prawdopodobnie nigdy nie będzie używane (tylko do odczytu)
                  setValue('paczkomat', e.target.value);
                }}
                placeholder="Wybierz paczkomat na mapie"
                readOnly
              />

              {errors.paczkomat && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.paczkomat.message}
                </p>
              )}
            </div>
          )}
        </FormControl>
      )}
    </div>
  );
};

export default DeliveryMethodForm;
